"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Project = exports.escape = void 0;
const core_1 = require("@actions/core");
const isomorphic_fetch_1 = __importDefault(require("isomorphic-fetch"));
const fetch_retry_1 = __importDefault(require("fetch-retry"));
const fetch = (0, fetch_retry_1.default)(isomorphic_fetch_1.default);
/**
 * RTD replaces '/' with '-' in the branch name.
 */
function escape(name) {
    if (name.indexOf("?") >= 0) {
        throw new Error(`name should not contains ? mark, but it was "${name}"`);
    }
    return name.replace(/\//g, "-").toLocaleLowerCase();
}
exports.escape = escape;
/**
 * A model representing the project in the Read The Docs.
 */
class Project {
    constructor(id, language, slug, translationOf) {
        this.id = id;
        this.language = language;
        this.slug = slug;
        this.translationOf = translationOf;
        if (id < 0) {
            throw new Error("the project ID cannot be negative");
        }
        if (language.length === 0) {
            throw new Error("The language cannot be blank");
        }
        if (slug.length === 0) {
            throw new Error("The slug cannot be blank");
        }
    }
    /**
     * @param branch name of the target branch
     * @returns a URL of the Read The Docs page for the given branch
     */
    createUrl(branch) {
        const slug = this.translationOf?.slug ?? this.slug;
        return new URL(`https://${escape(slug)}.readthedocs.io/${this.language}/${escape(branch)}/`);
    }
    /**
     * @param branch name of the target branch
     * @returns a URL of badge which displays the status of the given branch
     * @see https://docs.readthedocs.io/en/latest/badges.html
     */
    createBadge(branch) {
        if (branch.length === 0) {
            throw new Error("The branch cannot be blank");
        }
        return new URL(`https://readthedocs.org/projects/${escape(this.slug)}/badge/?version=${escape(branch)}`);
    }
}
exports.Project = Project;
function convertProject(raw) {
    const translationOf = raw.translation_of
        ? convertProject(raw.translation_of)
        : undefined;
    return new Project(raw.id, raw.language.code, raw.slug, translationOf);
}
class RTD {
    constructor(token) {
        this.token = token;
    }
    async getProject(slug) {
        const url = `https://readthedocs.org/api/v3/projects/${escape(slug)}/`;
        (0, core_1.debug)(`Fetching data of project ${slug} from ${url}`);
        return fetch(url, {
            method: "get",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${this.token}`,
            },
        })
            .then((res) => Promise.all([res.status, res.json()]))
            .then(([status, json]) => {
            if (status != 200) {
                throw new Error(`Unexpected status code ${status} with body: ${JSON.stringify(json)}`);
            }
            return convertProject(json);
        });
    }
    async getTranslates(project) {
        const projectInfo = typeof project === "string" ? await this.getProject(project) : project;
        const url = `https://readthedocs.org/api/v3/projects/${escape(projectInfo.slug)}/translations/`;
        (0, core_1.debug)(`Fetching data about traslates of ${project} from ${url}`);
        return fetch(url, {
            method: "get",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${this.token}`,
            },
        })
            .then((res) => {
            return Promise.all([res.status, res.json()]);
        })
            .then(([status, json]) => {
            if (status !== 200) {
                throw new Error(`Unexpected status code ${status} with body: ${JSON.stringify(json)}`);
            }
            return json["results"];
        })
            .then((translations) => {
            return translations.reduce((accumulator, currentValue) => {
                accumulator.push(convertProject(currentValue));
                return accumulator;
            }, [projectInfo]);
        });
    }
    /**
     * @see https://docs.readthedocs.io/en/stable/api/v3.html#version-detail
     */
    async getBuildActiveness(project, branch) {
        const url = `https://readthedocs.org/api/v3/projects/${project}/versions/${escape(branch)}/`;
        (0, core_1.debug)(`Fetchong build activeness of ${project} from ${url}`);
        return fetch(url, {
            method: "get",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${this.token}`,
            },
            retryOn: [400],
        })
            .then((res) => {
            return Promise.all([res.status, res.json()]);
        })
            .then(([status, json]) => {
            if (status != 200) {
                throw new Error(`Unexpected status code ${status} with body: ${JSON.stringify(json)}`);
            }
            return json;
        })
            .then((ver) => ver.active);
    }
    /**
     * @see https://docs.readthedocs.io/en/stable/api/v3.html#version-update
     */
    async configureBuild(project, branch, flag) {
        const currentFlag = await this.getBuildActiveness(project, branch);
        if (currentFlag === flag) {
            // no need to ask for update
            return false;
        }
        // this implementation doesn't care the transaction, so
        // the returned value could be different with expected one
        // if we run two procedures at the same time
        return fetch(`https://readthedocs.org/api/v3/projects/${project}/versions/${escape(branch)}/`, {
            method: "patch",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${this.token}`,
            },
            body: `{"active": ${flag}}`,
        })
            .then((res) => {
            if (res.status != 204) {
                return Promise.all([res.status, res.json()]);
            }
            else {
                // 204 responses empty text, so json() doesn't work
                return Promise.all([res.status, res.text()]);
            }
        })
            .then(([status, json]) => {
            if (status != 204) {
                throw new Error(`Unexpected status code ${status} with body: ${JSON.stringify(json)}`);
            }
            return true;
        });
    }
    async enableBuild(project, branch) {
        return this.configureBuild(project, branch, true);
    }
    async disableBuild(project, branch) {
        return this.configureBuild(project, branch, false);
    }
}
exports.default = RTD;
